import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Pagination } from '@/components/ui/Pagination'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Avatar } from '@/components/ui/Avatar'
import { useProjects } from '@/hooks/useProjects'
import { useDebounce } from '@/hooks/useDebounce'
import { formatDate, formatCurrency } from '@/lib/utils'
import {
  Plus,
  Search,
  FolderKanban,
  Calendar,
  DollarSign,
  Clock,
  Eye,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from 'lucide-react'
import type { ProjectStatus, Project } from '@/types/project'
import { PROJECT_STATUS_CONFIG } from '@/types/project'

const ITEMS_PER_PAGE = 20

function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const config = PROJECT_STATUS_CONFIG[status]
  return (
    <Badge variant={config.color}>
      {config.label}
    </Badge>
  )
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="w-full bg-beige-200 rounded-full h-2">
      <div
        className="bg-mint-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
}

function ProjectRow({ project }: { project: Project }) {
  const isOverdue = project.date_fin && new Date(project.date_fin) < new Date() &&
    project.status !== 'completed' && project.status !== 'cancelled'

  return (
    <tr className="hover:bg-beige-50 transition-colors">
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
            <FolderKanban className="w-5 h-5 text-mint-600" />
          </div>
          <div>
            <Link
              to={`/projects/${project.id}`}
              className="font-medium text-brown-900 hover:text-mint-600 transition-colors"
            >
              {project.nom}
            </Link>
            {project.description && (
              <p className="text-sm text-brown-500 line-clamp-1 max-w-xs">
                {project.description}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        {project.client ? (
          <div className="flex items-center gap-2">
            <Avatar
              src={project.client.avatar_url}
              name={project.client.full_name}
              size="sm"
            />
            <div>
              <p className="text-sm font-medium text-brown-900">
                {project.client.full_name}
              </p>
              {project.client.company && (
                <p className="text-xs text-brown-500">{project.client.company}</p>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm text-brown-400">-</span>
        )}
      </td>
      <td className="px-4 py-4">
        <ProjectStatusBadge status={project.status} />
      </td>
      <td className="px-4 py-4">
        {project.budget ? (
          <span className="text-sm font-medium text-brown-900">
            {formatCurrency(project.budget)}
          </span>
        ) : (
          <span className="text-sm text-brown-400">-</span>
        )}
      </td>
      <td className="px-4 py-4">
        {project.date_fin ? (
          <div className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-600' : 'text-brown-600'}`}>
            <Calendar className="w-4 h-4" />
            <span className="text-sm">
              {formatDate(project.date_fin, { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            {isOverdue && (
              <Clock className="w-4 h-4 text-red-500" />
            )}
          </div>
        ) : (
          <span className="text-sm text-brown-400">-</span>
        )}
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-[80px]">
            <ProgressBar value={project.progression} />
          </div>
          <span className="text-sm font-medium text-brown-700 w-10 text-right">
            {project.progression}%
          </span>
        </div>
      </td>
      <td className="px-4 py-4 text-right">
        <Link to={`/projects/${project.id}`}>
          <Button variant="ghost" size="sm">
            <Eye className="w-4 h-4" />
          </Button>
        </Link>
      </td>
    </tr>
  )
}

export default function ProjectsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('')
  const [page, setPage] = useState(1)
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useProjects({
    search: debouncedSearch,
    status: statusFilter || null,
    page,
    limit: ITEMS_PER_PAGE,
    sortBy,
    sortOrder,
  })

  function handleSort(col: string) {
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortOrder('desc')
    }
    setPage(1)
  }

  function SortIcon({ col }: { col: string }) {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-mint-600" />
      : <ChevronDown className="w-3.5 h-3.5 text-mint-600" />
  }

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Projets
          </h1>
          <p className="mt-1 text-brown-600">
            {data?.total || 0} projet{(data?.total || 0) > 1 ? 's' : ''}
          </p>
        </div>
        <div className="relative">
          <Button
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            rightIcon={<ChevronDown className="w-4 h-4" />}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Nouveau projet
          </Button>

          {showCreateMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowCreateMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-beige-200 py-2 z-20">
                <Link
                  to="/projects/new-ai"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-beige-50 transition-colors"
                  onClick={() => setShowCreateMenu(false)}
                >
                  <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-mint-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-brown-900">Avec assistant IA</p>
                    <p className="text-xs text-brown-600 mt-0.5">
                      Questions guidées et analyse intelligente
                    </p>
                  </div>
                </Link>

                <div className="border-t border-beige-200 my-1" />

                <Link
                  to="/projects/new"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-beige-50 transition-colors"
                  onClick={() => setShowCreateMenu(false)}
                >
                  <div className="w-10 h-10 rounded-lg bg-beige-100 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-5 h-5 text-brown-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-brown-900">Création rapide</p>
                    <p className="text-xs text-brown-600 mt-0.5">
                      Formulaire simple et direct
                    </p>
                  </div>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="search"
              placeholder="Rechercher un projet..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ProjectStatus | '')
                setPage(1)
              }}
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="in_progress">En cours</option>
              <option value="review">En revision</option>
              <option value="completed">Termine</option>
              <option value="cancelled">Annule</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-brown-500">En cours</p>
              <p className="text-xl font-semibold text-brown-900">
                {data?.projects?.filter(p => p.status === 'in_progress').length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-gold-600" />
            </div>
            <div>
              <p className="text-sm text-brown-500">En attente</p>
              <p className="text-xl font-semibold text-brown-900">
                {data?.projects?.filter(p => p.status === 'pending').length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-mint-600" />
            </div>
            <div>
              <p className="text-sm text-brown-500">Termines</p>
              <p className="text-xl font-semibold text-brown-900">
                {data?.projects?.filter(p => p.status === 'completed').length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-beige-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-brown-600" />
            </div>
            <div>
              <p className="text-sm text-brown-500">Budget total</p>
              <p className="text-xl font-semibold text-brown-900">
                {formatCurrency(
                  data?.projects?.reduce((sum, p) => sum + (p.budget || 0), 0) || 0
                )}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : data?.projects?.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<FolderKanban className="w-12 h-12" />}
            title="Aucun projet"
            description={
              search || statusFilter
                ? "Aucun projet ne correspond a vos criteres de recherche"
                : "Commencez par creer votre premier projet"
            }
            action={
              !search && !statusFilter && (
                <Link to="/projects/new">
                  <Button leftIcon={<Plus className="w-4 h-4" />}>
                    Creer un projet
                  </Button>
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  {([
                    { label: 'Projet', col: 'title' },
                    { label: 'Client', col: null },
                    { label: 'Statut', col: 'status' },
                    { label: 'Budget', col: 'budget' },
                    { label: 'Deadline', col: 'date_fin' },
                    { label: 'Progression', col: 'progression', minW: true },
                    { label: 'Actions', col: null, align: 'right' as const },
                  ] as { label: string; col: string | null; minW?: boolean; align?: 'right' }[]).map(({ label, col, minW, align }) => (
                    <th
                      key={label}
                      className={`text-${align ?? 'left'} px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider whitespace-nowrap ${minW ? 'min-w-[150px]' : ''} ${col ? 'cursor-pointer select-none hover:text-brown-700' : ''}`}
                      onClick={col ? () => handleSort(col) : undefined}
                    >
                      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
                        {label}
                        {col && <SortIcon col={col} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {data?.projects?.map((project) => (
                  <ProjectRow key={project.id} project={project} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
